import YAML from "yaml";

import { MetadataChangeset } from "../metadataChangeset";

export enum Accessibility {
  Organization = "organization",
  ActionHub = "action_hub",
}

export interface ComputeRequirements {
  vCPU: number;
  memory: number;
  storage: number;
  gpu: boolean;
}

export interface ContainerParameters {
  command: string[] | null;
  entry_point: string[] | null;
  env_vars: Record<string, string> | null;
  workdir: string | null;
}

export interface ActionReference {
  name: string;
  digest?: string;
  owner?: string;
}

export interface ActionParameter {
  name: string;
  required: boolean;
  description?: string;
  default?: unknown;
}

export interface ActionRecord {
  created: string;
  created_by: string;
  modified: string;
  modified_by: string;
  name: string;
  org_id: string;
  accessibility: Accessibility;
  compute_requirements?: ComputeRequirements;
  container_parameters: ContainerParameters;
  description?: string;
  digest?: string;
  inherits?: ActionReference;
  metadata: Record<string, string>;
  parameters?: ActionParameter[];
  published?: string;
  tags: string[];
  uri?: string;
  short_description?: string;
  timeout?: number;
  requires_downloaded_inputs: boolean | null; // defaults to true if unset
}

export function isActionRecord(obj: unknown): obj is ActionRecord {
  return typeof obj === "object" && obj !== null && "name" in obj;
}

export interface CreateActionRequest {
  name: string;
  compute_requirements?: ComputeRequirements | null;
  container_parameters?: ContainerParameters | null;
  description?: string;
  metadata?: Record<string, unknown>;
  parameters?: ActionParameter[];
  tags?: string[];
  // The action-timeout form field holds its raw text-input value, so a
  // numeric string may be sent; the backend coerces it.
  timeout?: number | string | null;
  uri?: string;
}

export interface ParameterChangeset {
  put_parameters?: ActionParameter[];
  remove_parameters?: string[];
}

export type UpdateActionRequest = Partial<{
  compute_requirements: ComputeRequirements;
  container_parameters: ContainerParameters;
  description: string;
  inherits: ActionReference;
  metadata_changeset: MetadataChangeset;
  parameter_changeset: ParameterChangeset;
  uri: string | null;
  short_description: string;
  timeout: number;
  requires_downloaded_inputs: boolean;
}>;

/**
 * Response from cancelling active invocations.
 */
export interface CancelActiveInvocationsResponse {
  /** Number of invocations successfully cancelled */
  success_count: number;
  /** Number of invocations that failed to be cancelled */
  failure_count: number;
  /** Whether there are more invocations to cancel (for pagination) */
  has_more: boolean;
}

export enum InvocationDataSourceType {
  Dataset = "Dataset",
  Unknown = "Unknown",
}

export interface InvocationDataSource {
  data_source_type: InvocationDataSourceType;
  data_source_id: string;
}

export const UNSPECIFIED_DATA_SOURCE: InvocationDataSource = {
  data_source_id: "__UNSPECIFIED_DATA_SOURCE__",
  data_source_type: InvocationDataSourceType.Dataset,
};

export function isDataSourceSpecified(
  dataSource: InvocationDataSource,
): boolean {
  // Note: this must match the SDK constant defined in invocation_record.py
  return dataSource.data_source_id !== UNSPECIFIED_DATA_SOURCE.data_source_id;
}

export enum UploadDestinationType {
  Dataset = "Dataset",
}

export interface InvocationUploadDestination {
  destination_type: UploadDestinationType;
  destination_id: string | null;
}

export enum InvocationStatus {
  Queued = 0,
  Scheduled = 1,
  Downloading = 2,
  Processing = 3,
  Uploading = 4,
  Completed = 5,
  Cancelled = 997,
  Failed = 998,
  Deadly = 999,
}

export const invocationStatusToString = (status?: InvocationStatus) => {
  switch (status) {
    case InvocationStatus.Queued:
      return "Pending";
    case InvocationStatus.Scheduled:
      return "Provisioning";
    case InvocationStatus.Downloading:
    case InvocationStatus.Processing:
    case InvocationStatus.Uploading: // Fall through
      return "Running";
    case InvocationStatus.Completed:
      return "Completed";
    case InvocationStatus.Cancelled:
      return "Cancelled";
    case InvocationStatus.Failed:
    case InvocationStatus.Deadly: // Fall through
      return "Failed";
  }
  return "Unknown";
};

export interface ActionProvenance {
  name: string;
  org_id: string;
  digest?: string;
}

export interface ExecutableProvenance {
  container_image_uri?: string;
  container_image_digest?: string;
}

export enum InvocationSource {
  Trigger = "Trigger",
  Manual = "Manual",
}

export interface SourceProvenance {
  source_type: InvocationSource;
  source_id: string;
}

export interface InvocationProvenance {
  action: ActionProvenance;
  executable: ExecutableProvenance;
  source: SourceProvenance;
}

export interface InvocationStatusRecord {
  status: InvocationStatus;
  detail?: string;
  timestamp: string;
}

export interface DataSelector {
  query: string | null;
  ids: string[] | null;
  names: string[] | null;
  dataset_id: string | null;
}

export interface FileSelector extends DataSelector {
  paths: string[] | null;
}

export interface InvocationInput {
  files: FileSelector | FileSelector[] | null;
  topics: DataSelector | DataSelector[] | null;
}

const EMPTY_INVOCATION_INPUT: InvocationInput = {
  files: null,
  topics: null,
};

const EMPTY_DATA_SELECTOR: DataSelector = {
  query: null,
  names: null,
  ids: null,
  dataset_id: null,
};

const EMPTY_FILE_SELECTOR: FileSelector = {
  ...EMPTY_DATA_SELECTOR,
  paths: null,
};

export class InvocationInputs {
  static fileQuery(query: string): InvocationInput {
    return {
      files: {
        ...EMPTY_FILE_SELECTOR,
        query: query,
      },
      topics: null,
    };
  }

  static topicQuery(query: string): InvocationInput {
    return {
      topics: {
        ...EMPTY_DATA_SELECTOR,
        query: query,
      },
      files: null,
    };
  }
}

export interface InvocationRecord {
  created: string;
  data_source: InvocationDataSource;
  input_data: string[];
  rich_input_data: InvocationInput | null;
  invocation_id: string;
  idempotency_id?: string;
  compute_requirements: ComputeRequirements;
  container_parameters: ContainerParameters;
  last_status: InvocationStatus;
  org_id: string;
  parameter_values: Record<string, string>;
  provenance: InvocationProvenance;
  status: InvocationStatusRecord[];
  duration: string;
  timeout: number | null;
  upload_destination: InvocationUploadDestination | null;
}

export function isInvocationRecord(obj: unknown): obj is InvocationRecord {
  return typeof obj === "object" && obj !== null && "invocation_id" in obj;
}

export function getInvocationInput(
  invocation: InvocationRecord,
  includeDatasetId: boolean = false,
): InvocationInput {
  if (invocation.rich_input_data) {
    return invocation.rich_input_data;
  }

  if (isDataSourceSpecified(invocation.data_source)) {
    return {
      files: {
        ...EMPTY_FILE_SELECTOR,
        paths: invocation.input_data,
        dataset_id: includeDatasetId
          ? invocation.data_source.data_source_id
          : null,
      },
      topics: null,
    };
  }

  return EMPTY_INVOCATION_INPUT;
}

export function invocationInputToYaml(
  invocationInput: InvocationInput,
): string {
  if (invocationInput === EMPTY_INVOCATION_INPUT) {
    return "None";
  }

  return YAML.stringify(invocationInput, (_key, value) => {
    // Filter out null fields from the input spec.
    if (value === null) {
      return undefined;
    }

    return value;
  });
}

export enum InvocationProcess {
  Setup = "setup",
  Action = "action",
  OutputHandler = "output_handler",
}

export interface InvocationLogRecord {
  partial_id?: string;
  log: string;
  process: InvocationProcess;
  timestamp: string;
}
